import { ServerConnectionModule } from "@modules/multiplayer/server/ServerConnectionModule";
import { PeerModule } from "@modules/multiplayer/peer/PeerModule";
import { SessionModule } from "@modules/multiplayer/session/SessionModule";
import { AvatarLoaderModule } from "@modules/avatar/AvatarLoaderModule";
import { RendererModule } from "@modules/rendering/renderer/RendererModule";
import { BooleanMeshOperationModule } from "@modules/booleanMeshOperation/BooleanMeshOperationModule";
import { workerFactory, ViewInitModule } from "@modules/app";
import { ControllerModule } from "@modules/inputs/controller/ControllerModule";
import { MeshLoaderModule } from "@modules/rendering/mesh/MeshLoaderModule";
import { LocalPlayerModule } from "@modules/localplayer/LocalPlayerModule";
import { WebSpeechSTT } from "@modules/stt/WebSpeechSTT";
import { SpeechParser } from "@modules/stt/SpeechParser";
import { DebugWireframeRendererModule } from "@modules/debugWireframe/DebugWireframeRendererModule";
import { VrToastModule } from "@modules/toast/VrToastModule";
import { GestureModule } from "@modules/gesture/GestureModule";
import { ParticleModule } from "@modules/particle/ParticleModule";
import { BackgroundModule } from "@modules/rendering/background/BackgroundModule";
import { MarkerModule } from "@modules/marker/MarkerModule";
import { HTMLCanvasCursorModule } from "@modules/htmlCanvasCursor/HTMLCanvasCursorModule";
import { WebXRModule } from "@modules/xr/WebXRModule";
import { ScreenshotModule } from "@modules/screenshot/ScreenshotModule";
import { hrefHasParameter, PrimitiveMeshFacet, AxisHelperFacet, MeshFacet, MapFacet, TextFacet, ImageFacet, AvatarFacet, ControllerFacet, ArcFacet, LineFacet, HTMLCanvasFacet, Tooltip, LabelFacet, LineStrip, Annotations, RaycastLine, ProximitySphere, RecordingIndicator, AudioSourceFacet, LightSource, TooltipInfoFacet, TruncatedConeFacet, RopeFacet, MovingTextureFacet, ClippingMeshFacet, RotatingFacet, ReflectionProbeFacet, HideWhenQualityFacet, MediaScreenFacet, TextToTextureFacet, CameraRecorderViewFacet, NavMeshFacet, ImageToTextureFacet, PdfToTextureFacet, PointCloudFacet, StartPointFacet, MovingToFacet, MirrorFacet, MeshGroupFacet, TrainingVideoFacet } from "flavr-common";
import { RenderableFactory } from "@modules/rendering/renderable/RenderableFactory";
import { PrimitiveMeshRenderable } from "@modules/rendering/primitives/PrimitiveMeshRenderable";
import { AxisHelperRenderable } from "@modules/rendering/axisHelper/AxisHelperRenderable";
import { MeshRenderable } from "@modules/rendering/mesh/MeshRenderable";
import { TextRenderable } from "@modules/rendering/primitives/TextRenderable";
import { AvatarRenderable } from "@modules/avatar/AvatarRenderable";
import { ControllerRenderable } from "@modules/rendering/controller/ControllerRenderable";
import { ArcRenderable } from "@modules/rendering/primitives/ArcRenderable";
import { MapRenderable } from "@modules/map";
import { ImageRenderable } from "@modules/rendering/image/ImageRenderable";
import { TruncatedConeRenderable } from "@modules/rendering/primitives/TruncatedConeRenderable";
import { LineRenderable } from "@modules/rendering/line/LineRenderable";
import { HTMLCanvasRenderable } from "@modules/htmlCanvasRendering";
import { RendererFacetFactory } from "@modules/rendering/renderable/RendererFacetFactory";
import { TooltipRenderable } from "@modules/rendering/tooltip/TooltipRenderable";
import { LabelRenderable } from "@modules/label";
import { LineStripRenderable } from "@modules/rendering/line/LineStripRenderable";
import { AnnotationRenderable } from "@modules/annotation";
import { RaycastLineRenderable } from "@modules/rendering/line/RaycastLineRenderable";
import { ProximitySphereRenderable } from "@modules/rendering/line/ProximitySphereRenderable";
import { RecordIndicatorRenderer } from "@modules/recording";
import { AudioSourceRenderable } from "@modules/audio";
import { LightSourceRenderable } from "@modules/rendering/light/LightSourceRenderable";
import { TooltipInfoRenderer } from "@modules/rendering/tooltip/TooltipInfoRenderer";
import { RopeRenderable } from "@modules/rendering/line/RopeRenderable";
import { MovingTextureRenderable } from "@modules/rendering/texture/MovingTextureRenderable";
import { ClippingMeshRenderable } from "@modules/clipping";
import { RotatingRenderable } from "@modules/rotatingMesh";
import { ModelPickupModule } from "@modules/pickup/ModelPickupModule";
import { AutoPixelRatioModule } from "@modules/auto/AutoPixelRatioModule";
import { ReflectionProbeRenderable } from "@modules/rendering/reflectionProbe/ReflectionProbeRenderable";
import { HideWhenQualityRenderable } from "@modules/rendering/material/HideWhenQualityRenderable";
import { MediaScreenRenderable } from "@modules/mediaScreen";
import { NetworkStatusModule } from "@modules/network";
import { AutoChoosePlayerForDeviceModule } from "@modules/auto/AutoChoosePlayerForDeviceModule";
import { TextToTextureRenderable } from "@modules/rendering/texture/TextToTextureRenderable";
import { CameraRecorderViewRenderable } from "@modules/rendering/camera/CameraRecorderViewRenderable";
import { NavMeshRenderable } from "@modules/navMesh";
import { ImageToTextureRenderable } from "@modules/rendering/texture/ImageToTextureRenderable";
import { PdfToTextureRenderable } from "@modules/rendering/texture/PdfToTextureRenderable";
import { PointCloudRenderable } from "@modules/rosbag";
import { ToolViewerModule } from "@modules/blueprint/ToolViewerModule";
import { BlueprintModule } from "@modules/blueprint/BlueprintModule";
import { TeleporterModule } from "@modules/teleporter/TeleporterModule";
import { StartPointRenderable } from "@modules/rendering/startPoint/StartPointRenderable";
import { UIModule } from "./ui/UIModule";
import { MovingToRenderable } from "@modules/moveTo";
import { MirrorRenderable } from "@modules/mirror";
import { GizmoModule } from "@modules/gizmo/GizmoModule";
import { CollisionModule } from "@modules/selection/collision/CollisionModule";
import { InteractionModule } from "@modules/interaction";
import { ScriptModule } from "@modules/scripting";
import { TrainingOrchestratorModule, TrainingVideoRenderable } from "@modules/training";
import { MeshGroupRenderable } from "@modules/rendering/mesh/MeshGroupRenderable";
import { ModelIndicatorModule } from "@modules/indicator";
// import { DebugModelBoundsModule } from "@modules/debug/DebugModelBoundsModule"
import { LayouterModule } from "@modules/layouters";
import { BillboardModule } from "@modules/billboard";
import { ModelDragSyncModule } from "@modules/gizmo/ModelDragSyncModule";
import { MovementConstraintModule } from "@modules/movementConstraint/MovementConstraintModule";
import { BoundsModule } from "@modules/bounds";
import { LinkingModule } from "@modules/linking/LinkingModule";
import { UI3DRenderable } from "@modules/ui3d/UI3DRenderable";
import { UI3DModule } from "@modules/ui3d/UI3DModule";
import { UI3DFacet } from "flavr-common";
const ENABLE_DEBUG_WIREFRAME = hrefHasParameter(location.href, "debugwireframe");
workerFactory.clearWorkerRegistry();
// create workers
workerFactory.registerWorker("roomManager");
workerFactory.registerDedicatedWorker("PointCloudSharedArrayWorker", new Worker(new URL("./modules/rosbag/PointCloudSharedArrayWorker.ts", import.meta.url), {
    credentials: "same-origin",
    name: "PointCloudSharedArrayWorker"
}));
workerFactory.clearModuleRegistry();
workerFactory.registerModule(PeerModule);
workerFactory.registerModule(AvatarLoaderModule);
workerFactory.registerModule(GestureModule);
workerFactory.registerModule(LocalPlayerModule);
workerFactory.registerModule(RendererModule);
workerFactory.registerModule(BooleanMeshOperationModule);
workerFactory.registerModule(MarkerModule);
if (ENABLE_DEBUG_WIREFRAME) {
    workerFactory.registerModule(DebugWireframeRendererModule);
}
workerFactory.registerModule(SessionModule);
workerFactory.registerModule(ServerConnectionModule);
workerFactory.registerModule(BackgroundModule);
workerFactory.registerModule(MeshLoaderModule);
workerFactory.registerModule(ControllerModule);
workerFactory.registerModule(ViewInitModule);
workerFactory.registerModule(SpeechParser);
workerFactory.registerModule(WebSpeechSTT);
workerFactory.registerModule(VrToastModule);
workerFactory.registerModule(ParticleModule);
workerFactory.registerModule(NetworkStatusModule);
workerFactory.registerModule(HTMLCanvasCursorModule);
workerFactory.registerModule(UIModule);
workerFactory.registerModule(WebXRModule);
workerFactory.registerModule(ScreenshotModule);
workerFactory.registerModule(BoundsModule);
workerFactory.registerModule(ModelPickupModule);
workerFactory.registerModule(LinkingModule);
workerFactory.registerModule(AutoPixelRatioModule);
workerFactory.registerModule(AutoChoosePlayerForDeviceModule);
workerFactory.registerModule(ToolViewerModule);
workerFactory.registerModule(BlueprintModule);
workerFactory.registerModule(TeleporterModule);
workerFactory.registerModule(GizmoModule);
workerFactory.registerModule(TrainingOrchestratorModule);
workerFactory.registerModule(CollisionModule);
workerFactory.registerModule(InteractionModule);
workerFactory.registerModule(ScriptModule);
workerFactory.registerModule(ModelIndicatorModule);
workerFactory.registerModule(BillboardModule);
workerFactory.registerModule(ModelDragSyncModule);
workerFactory.registerModule(MovementConstraintModule);
// workerFactory.registerModule(DebugModelBoundsModule)
workerFactory.registerModule(LayouterModule);
RenderableFactory.register(PrimitiveMeshFacet, PrimitiveMeshRenderable);
RenderableFactory.register(MeshFacet, MeshRenderable);
RenderableFactory.register(MapFacet, MapRenderable);
RenderableFactory.register(TextFacet, TextRenderable);
RenderableFactory.register(AvatarFacet, AvatarRenderable);
RenderableFactory.register(ControllerFacet, ControllerRenderable);
RenderableFactory.register(ArcFacet, ArcRenderable);
RenderableFactory.register(TruncatedConeFacet, TruncatedConeRenderable);
RenderableFactory.register(LineFacet, LineRenderable);
RenderableFactory.register(HTMLCanvasFacet, HTMLCanvasRenderable);
RenderableFactory.register(RopeFacet, RopeRenderable);
RenderableFactory.register(ImageFacet, ImageRenderable);
RenderableFactory.register(MirrorFacet, MirrorRenderable);
RenderableFactory.register(MeshGroupFacet, MeshGroupRenderable);
RenderableFactory.register(TrainingVideoFacet, TrainingVideoRenderable);
workerFactory.registerModule(UI3DModule);
RenderableFactory.register(UI3DFacet, UI3DRenderable);
RendererFacetFactory.register(MovingToFacet, MovingToRenderable);
RendererFacetFactory.register(AxisHelperFacet, AxisHelperRenderable);
RendererFacetFactory.register(PointCloudFacet, PointCloudRenderable);
RendererFacetFactory.register(Tooltip, TooltipRenderable);
RendererFacetFactory.register(LabelFacet, LabelRenderable);
RendererFacetFactory.register(LineStrip, LineStripRenderable);
RendererFacetFactory.register(Annotations, AnnotationRenderable);
RendererFacetFactory.register(RaycastLine, RaycastLineRenderable);
RendererFacetFactory.register(ProximitySphere, ProximitySphereRenderable);
RendererFacetFactory.register(RecordingIndicator, RecordIndicatorRenderer);
RendererFacetFactory.register(AudioSourceFacet, AudioSourceRenderable);
RendererFacetFactory.register(LightSource, LightSourceRenderable);
RendererFacetFactory.register(TooltipInfoFacet, TooltipInfoRenderer);
RendererFacetFactory.register(MovingTextureFacet, MovingTextureRenderable);
RendererFacetFactory.register(ClippingMeshFacet, ClippingMeshRenderable);
RendererFacetFactory.register(RotatingFacet, RotatingRenderable);
RendererFacetFactory.register(ReflectionProbeFacet, ReflectionProbeRenderable);
RendererFacetFactory.register(HideWhenQualityFacet, HideWhenQualityRenderable);
RendererFacetFactory.register(MediaScreenFacet, MediaScreenRenderable);
RendererFacetFactory.register(TextToTextureFacet, TextToTextureRenderable);
RendererFacetFactory.register(CameraRecorderViewFacet, CameraRecorderViewRenderable);
RendererFacetFactory.register(NavMeshFacet, NavMeshRenderable);
RendererFacetFactory.register(ImageToTextureFacet, ImageToTextureRenderable);
RendererFacetFactory.register(PdfToTextureFacet, PdfToTextureRenderable);
RendererFacetFactory.register(StartPointFacet, StartPointRenderable);
